import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import getHours from 'date-fns/getHours';
import parse from 'date-fns/parse';
import differenceInDays from 'date-fns/differenceInDays';
import { dateFilterParams, headerInfoTooltipDef, getValueBgClass, getPureNumber, dateValueGetter } from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

export default [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    headerName: 'Date of Trade',
    ...headerInfoTooltipDef('Date Trade was Executed'),
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    initialWidth: 110,
    valueFormatter: ({ value }) => formatDate(parseISO(value), 'MM/dd/yyyy'),
    valueGetter: ({ data }) => dateValueGetter(data.date),
  },
  {
    cellClass: 'ticker-cell',
    field: 'time',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: 'AM',
          rules: (value: number) => {
            return getHours(value) < 13;
          },
        },
        {
          label: 'PM',
          rules: (value: number) => {
            return getHours(value) >= 13;
          },
        },
      ],
    },
    headerName: 'Time',
    initialWidth: 70,
    valueFormatter: ({ value }) => {
      if (!value) return '';

      return formatDate(value, 'h:mm a');
    },
    valueGetter: ({ data }) => {
      if (!data.time) return '';

      return parse(data.time, 'HH:mm:ss', new Date());
    },
    ...headerInfoTooltipDef('Time of Call'),
  },
  {
    field: 'ticker',
    filter: 'agSetColumnFilter',
    ...headerInfoTooltipDef('A Unique Series of Letters Assigned to a Security for Trading Purposes'),
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
  },
  {
    field: 'put_call',
    headerName: 'Put/Call',
    ...headerInfoTooltipDef(
      'A Call Option Gives You The Right To Buy a Stock At A Certain Price, and a Put Give You The Option to Sell a Stock at a Certain Price',
    ),
    cellClass: 'ticker-cell',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
  },
  {
    field: 'strike_price',
    headerName: 'Strike Price',
    ...headerInfoTooltipDef('The Price at Which the Holder Can Buy/Sell the Underlying Equity when exercised.'),
    cellClass: ({ data }) => {
      let value = 0;

      if (data.sentiment === 'BULLISH') {
        value = 1;
      } else if (data.sentiment === 'BEARISH') {
        value = -1;
      }

      return getValueBgClass(value);
    },
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '$0 - $50',
          rules: (value: number) => {
            return value <= 50;
          },
        },
        {
          label: '$51 - $200',
          rules: (value: number) => {
            return value >= 51 && value <= 200;
          },
        },
        {
          label: '$201 - $500',
          rules: (value: number) => {
            return value >= 201 && value <= 500;
          },
        },
        {
          label: '$500 - $1000',
          rules: (value: number) => {
            return value >= 500 && value <= 1000;
          },
        },
        {
          label: 'Greater than 1000',
          rules: (value: number) => {
            return value >= 1000;
          },
        },
      ],
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => {
      if (!value) return '-';

      return `$${Number(value).toFixed(2)}`;
    },
  },
  {
    field: 'date_expiration',
    headerName: 'Exp Date',
    ...headerInfoTooltipDef('The Option Expiration Date'),
    cellClass: 'ticker-cell',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => {
      if (value) {
        return formatDate(parseISO(value), 'MM/dd/yyyy');
      }
      return '-';
    },
    valueGetter: ({ data }) => dateValueGetter(data.date_expiration),
  },
  {
    field: 'date_expiration',
    headerName: 'DTE',
    ...headerInfoTooltipDef('Number of Days Until Option Expires'),
    cellClass: 'ticker-cell',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '0 - 50',
          rules: (value: number) => {
            return value <= 50;
          },
        },
        {
          label: '51 - 100',
          rules: (value: number) => {
            return value >= 51 && value <= 100;
          },
        },
        {
          label: '100 - 200',
          rules: (value: number) => {
            return value >= 100 && value <= 200;
          },
        },
        {
          label: '201 - 300',
          rules: (value: number) => {
            return value >= 201 && value <= 300;
          },
        },
        {
          label: 'Greater than 300',
          rules: (value: number) => {
            return value >= 300;
          },
        },
      ],
    },
    initialWidth: 60,
    valueGetter: ({ data }) => {
      if (!data.date_expiration) return 0;

      return Number(differenceInDays(new Date(data.date_expiration), new Date()));
    },
  },
  {
    field: 'size',
    headerName: 'Trade Vol',
    ...headerInfoTooltipDef(
      'The amount of shares or contracts traded in an asset or security over a period of time, usually over the course of a trading day.',
    ),
    cellClass: 'ticker-cell',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '0 - 500',
          rules: (value: number) => {
            return value <= 500;
          },
        },
        {
          label: '501 - 2,000',
          rules: (value: number) => {
            return value >= 501 && value <= 2000;
          },
        },
        {
          label: '2,001 - 5,000',
          rules: (value: number) => {
            return value >= 2001 && value <= 5000;
          },
        },
        {
          label: '5000 - 10,000',
          rules: (value: number) => {
            return value >= 5000 && value <= 10000;
          },
        },
        {
          label: 'Greater than 10,000',
          rules: (value: number) => {
            return value >= 10000;
          },
        },
      ],
    },
    initialWidth: 90,
    valueFormatter: ({ value }) => {
      if (!value) return '-';

      return Number(value).toLocaleString('en');
    },
    valueGetter: ({ data }) => getPureNumber(data.size),
  },
  {
    field: 'volume',
    headerName: 'Day Vol',
    ...headerInfoTooltipDef('The Total Number of Shares or Contracts Traded for the Day.'),
    cellClass: 'ticker-cell',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '0 - 5000',
          rules: (value: number) => {
            return value <= 5000;
          },
        },
        {
          label: '5,001 - 20,000',
          rules: (value: number) => {
            return value >= 5001 && value <= 20000;
          },
        },
        {
          label: '20,001 - 50,000',
          rules: (value: number) => {
            return value >= 20001 && value <= 50000;
          },
        },
        {
          label: '50,001 - 100,000',
          rules: (value: number) => {
            return value >= 50001 && value <= 100000;
          },
        },
        {
          label: 'Greater than 100,000',
          rules: (value: number) => {
            return value >= 100000;
          },
        },
      ],
    },
    initialWidth: 85,
    valueFormatter: ({ value }) => {
      if (!value) return '-';

      return Number(value).toLocaleString('en');
    },
    valueGetter: ({ data }) => getPureNumber(data.volume),
  },
  {
    field: 'open_interest',
    headerName: 'Vol/OI',
    ...headerInfoTooltipDef(
      'Today’s Volume divided by the Open Interest.  A Higher Ratio Points to Unusual Activity on the Option.',
    ),
    cellClass: 'ticker-cell',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '0 - 50',
          rules: (value: number) => {
            return value <= 50;
          },
        },
        {
          label: '51 - 100',
          rules: (value: number) => {
            return value >= 51 && value <= 100;
          },
        },
        {
          label: '101 - 300',
          rules: (value: number) => {
            return value >= 101 && value <= 300;
          },
        },
        {
          label: '301 - 500',
          rules: (value: number) => {
            return value >= 301 && value <= 500;
          },
        },
        {
          label: 'Greater than 500',
          rules: (value: number) => {
            return value >= 500;
          },
        },
      ],
    },
    initialWidth: 70,
    valueGetter: ({ data }) => {
      if (!data.volume || !data.open_interest) return 0;

      return Number(((data.volume / data.open_interest) * 100).toFixed());
    },
  },
  {
    field: 'open_interest',
    headerName: 'OI',
    ...headerInfoTooltipDef('Total Number of Option Contracts that have been traded but not liquidated.'),
    cellClass: 'ticker-cell',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: '0 - 5000',
          rules: (value: number) => {
            return value <= 5000;
          },
        },
        {
          label: '5,001 - 20,000',
          rules: (value: number) => {
            return value >= 5001 && value <= 20000;
          },
        },
        {
          label: '20,001 - 50,000',
          rules: (value: number) => {
            return value >= 20001 && value <= 50000;
          },
        },
        {
          label: '50,001 - 100,000',
          rules: (value: number) => {
            return value >= 50001 && value <= 100000;
          },
        },
        {
          label: 'Greater than 100,000',
          rules: (value: number) => {
            return value >= 100000;
          },
        },
      ],
    },
    initialWidth: 60,
    valueFormatter: ({ value }) => {
      if (!value) return '-';

      return Number(value).toLocaleString('en');
    },
    valueGetter: ({ data }) => getPureNumber(data.open_interest),
  },
  {
    field: 'underlying_type',
    headerName: 'Trade Type',
    ...headerInfoTooltipDef('Trade Type'),
    cellClass: 'ticker-cell',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
  },
  {
    field: 'sentiment',
    headerName: 'Sentiment',
    ...headerInfoTooltipDef('Sentiment'),
    cellClass: 'ticker-cell',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
